/**
 * Approved seats on one opportunity: individuals without a group,
 * linked group sizes, and an approved group that has no group-opportunity row.
 * Same formula as the volunteer feed.
 */
export function opportunityOccupiedSeatsSql(opportunityIdExpr: string): string {
  return `(
    COALESCE((
      SELECT COUNT(*)::int
      FROM "opportunity-participant" op
      WHERE op.opportunity_id = ${opportunityIdExpr}
        AND op.status = 'APPROVED'::"ParticipantStatus"
        AND (op.group_id IS NULL OR op.group_id = '')
    ), 0)
    + COALESCE((
      SELECT SUM(g.size)::int
      FROM "group-opportunity" go
      INNER JOIN "group" g ON g.id = go.group_id
      WHERE go.opportunity_id = ${opportunityIdExpr}
    ), 0)
    + COALESCE((
      SELECT SUM(orphan.sz)::int
      FROM (
        SELECT MAX(COALESCE(g.size, 1)) AS sz
        FROM "opportunity-participant" AS op
        LEFT JOIN "group" AS g ON g.id = op.group_id
        WHERE op.opportunity_id = ${opportunityIdExpr}
          AND op.status = 'APPROVED'::"ParticipantStatus"
          AND op.group_id IS NOT NULL
          AND op.group_id <> ''
          AND NOT EXISTS (
            SELECT 1
            FROM "group-opportunity" AS go
            WHERE go.opportunity_id = op.opportunity_id
              AND go.group_id = op.group_id
          )
        GROUP BY op.group_id
      ) AS orphan
    ), 0)
  )`;
}

/** Occupied seats are still under the listed amount. Does not look at is_full. */
export function opportunityBelowCapacitySql(tableRef: string): string {
  return `${opportunityOccupiedSeatsSql(`${tableRef}.id`)} < COALESCE(${tableRef}.amount, 1)`;
}

/** Occupied seats have reached the listed amount. */
export function opportunityAtCapacitySql(tableRef: string): string {
  return `${opportunityOccupiedSeatsSql(`${tableRef}.id`)} >= COALESCE(${tableRef}.amount, 1)`;
}

/** Set is_full once occupied seats reach amount. Leaves a manual full mark alone. */
export function markOpportunityFullIfAtCapacitySql(opportunityIdSql: string): string {
  return `
    UPDATE "opportunity" AS o
    SET is_full = true
    WHERE o.id = ${opportunityIdSql}
      AND o.is_deleted = false
      AND o.is_full = false
      AND ${opportunityAtCapacitySql("o")}
  `;
}

/** Clear is_full after a seat opens and the row is under amount. */
export function reopenOpportunityIfBelowCapacitySql(opportunityIdSql: string): string {
  return `
    UPDATE "opportunity" AS o
    SET is_full = false
    WHERE o.id = ${opportunityIdSql}
      AND o.is_full = true
      AND NOT (${opportunityAtCapacitySql("o")})
  `;
}

export const MARK_OPPORTUNITIES_FULL_AT_CAPACITY_SQL = `
  UPDATE "opportunity" AS o
  SET is_full = true
  WHERE o.is_deleted = false
    AND o.is_full = false
    AND ${opportunityAtCapacitySql("o")}
`;

/** Mark one group parent full when every live child is closed or already full. */
export function markGroupParentFullSql(parentIdSql: string): string {
  return `
    UPDATE "opportunity" AS parent
    SET is_full = true
    WHERE parent.id = ${parentIdSql}
      AND parent.is_deleted = false
      AND parent.is_full = false
      AND parent.is_opportunity_group_parent = true
      AND EXISTS (
        SELECT 1
        FROM "opportunity" AS child
        WHERE child.parent_opportunity_id = parent.id
          AND child.is_deleted = false
      )
      AND NOT EXISTS (
        SELECT 1
        FROM "opportunity" AS child
        WHERE child.parent_opportunity_id = parent.id
          AND child.is_deleted = false
          AND child.is_closed = false
          AND child.is_full = false
      )
  `;
}

/** Reopen a group parent when one of its children has a free seat again. */
export function reopenGroupParentIfChildOpenSql(parentIdSql: string): string {
  return `
    UPDATE "opportunity" AS parent
    SET is_full = false
    WHERE parent.id = ${parentIdSql}
      AND parent.is_full = true
      AND EXISTS (
        SELECT 1
        FROM "opportunity" AS child
        WHERE child.parent_opportunity_id = parent.id
          AND child.is_deleted = false
          AND child.is_closed = false
          AND child.is_full = false
      )
  `;
}

export const MARK_GROUP_PARENTS_FULL_SQL = `
  UPDATE "opportunity" AS parent
  SET is_full = true
  WHERE parent.is_deleted = false
    AND parent.is_full = false
    AND parent.is_opportunity_group_parent = true
    AND EXISTS (
      SELECT 1
      FROM "opportunity" AS child
      WHERE child.parent_opportunity_id = parent.id
        AND child.is_deleted = false
    )
    AND NOT EXISTS (
      SELECT 1
      FROM "opportunity" AS child
      WHERE child.parent_opportunity_id = parent.id
        AND child.is_deleted = false
        AND child.is_closed = false
        AND child.is_full = false
    )
`;

/** Not manually marked full, and still has a free seat. */
export function opportunityHasFreeSeatsSql(tableRef: string): string {
  return `(
    ${tableRef}.is_full = false
    AND ${opportunityBelowCapacitySql(tableRef)}
  )`;
}

/**
 * Group parent whose remaining instances are all full by flag or by seats.
 * Closed instances are ignored. A parent with no open instance is not matched.
 */
export function opportunityGroupParentSeatsFullSql(tableRef: string): string {
  return `(
    ${tableRef}.is_opportunity_group_parent = true
    AND EXISTS (
      SELECT 1
      FROM "opportunity" AS child_full
      WHERE child_full.parent_opportunity_id = ${tableRef}.id
        AND child_full.is_deleted = false
        AND child_full.is_closed = false
    )
    AND NOT EXISTS (
      SELECT 1
      FROM "opportunity" AS child_open
      WHERE child_open.parent_opportunity_id = ${tableRef}.id
        AND child_open.is_deleted = false
        AND child_open.is_closed = false
        AND ${opportunityHasFreeSeatsSql("child_open")}
    )
  )`;
}

export function opportunitySeatsAreFull(input: {
  isFull?: boolean | null;
  participantsCount?: number | null;
  amount?: number | null;
}): boolean {
  if (input.isFull) return true;
  if (input.amount == null || input.participantsCount == null) return false;
  return input.participantsCount >= input.amount;
}
